import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { resetPassword } from '../services/authService';

const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Extraire le token de l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Token de réinitialisation manquant');
    }
  }, [location]);

  // Fonction de validation du mot de passe
  const validatePassword = (pass) => {
    if (pass.length < 8) return false;
    
    // Vérification de la présence d'une majuscule, d'une minuscule et d'un chiffre
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasDigit = /\d/.test(pass);
    
    return hasUpperCase && hasLowerCase && hasDigit;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation améliorée du mot de passe
    if (!validatePassword(password)) {
      setError('Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);

    try {
      // Utilisation du service d'authentification
      await resetPassword(token, password);
      
      setSuccess('Mot de passe réinitialisé avec succès !');
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login', { state: { message: 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.' } });
      }, 3000);
    } catch (err) {
      // Gestion d'erreur
      const errorMessage = err.response?.data?.detail || 'Erreur lors de la réinitialisation du mot de passe';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-slate mb-6 text-center">Réinitialiser votre mot de passe</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        
        {!token && !success ? (
          <div className="text-center">
            <p className="mb-4">Le lien de réinitialisation est invalide ou a expiré.</p>
            <Link to="/forgot-password" className="text-terracotta hover:underline">
              Demander un nouveau lien
            </Link>
          </div>
        ) : !success && (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-slate font-medium mb-2" htmlFor="password">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
              />
              <p className="text-xs text-gray-500 mt-1">
                Au moins 8 caractères, une majuscule, une minuscule et un chiffre.
              </p>
            </div>
            
            <div className="mb-6">
              <label className="block text-slate font-medium mb-2" htmlFor="confirmPassword">
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50"
            >
              {loading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;